import { Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { Crosshair, Hammer, Library, Search, Server, ShieldCheck, TerminalSquare } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { alpha, colors } from '../../theme/tokens';
import { selectRole, useAuthStore, type UserRole } from '../../store/useAuthStore';
import { useCommandPalette } from '../../store/useCommandPalette';

/**
 * CommandPalette — global Cmd/Ctrl+K launcher.
 *
 * Pure client-side fuzzy match across a fixed action list; bound to
 * `keydown` on window. Closes on action select, Escape, or backdrop click.
 *
 * Performance: the action list is static, so filter/render cost stays
 * constant — no debounce needed.
 */

interface ActionItem {
  id: string;
  label: string;
  hint: string;
  icon: JSX.Element;
  to: string;
  /** Role gate — mirrors NavConfig. Omit for actions visible to every role. */
  roles?: ReadonlyArray<UserRole>;
}

const INSTRUCTOR_UP: ReadonlyArray<UserRole> = ['instructor', 'org_admin', 'superadmin'];
const ADMIN_UP: ReadonlyArray<UserRole> = ['org_admin', 'superadmin'];

const ACTIONS: ReadonlyArray<ActionItem> = [
  { id: 'scenarios', label: 'Browse scenarios', hint: 'catalog', icon: <Crosshair size={14} />, to: '/scenarios', roles: INSTRUCTOR_UP },
  { id: 'ranges', label: 'My ranges', hint: 'deployed', icon: <Server size={14} />, to: '/ranges', roles: INSTRUCTOR_UP },
  { id: 'courses', label: 'Courses', hint: 'curriculum', icon: <Library size={14} />, to: '/courses' },
  { id: 'labs', label: 'Labs', hint: 'fargate', icon: <TerminalSquare size={14} />, to: '/labs' },
  { id: 'builder', label: 'Scenario builder', hint: 'authoring', icon: <Hammer size={14} />, to: '/builder', roles: INSTRUCTOR_UP },
  { id: 'cost', label: 'Cost dashboard', hint: 'spend', icon: <ShieldCheck size={14} />, to: '/cost', roles: ADMIN_UP },
  { id: 'audit', label: 'Audit log', hint: 'governance', icon: <ShieldCheck size={14} />, to: '/admin/audit', roles: ADMIN_UP },
];

export function CommandPalette() {
  const open = useCommandPalette((s) => s.open);
  const setOpen = useCommandPalette((s) => s.setOpen);
  const toggle = useCommandPalette((s) => s.toggle);
  const role = useAuthStore(selectRole);
  const [q, setQ] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (cmdK) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  // Role-gate first (trainees never see instructor/admin commands), then fuzzy match.
  const allowed = useMemo(
    () => ACTIONS.filter((a) => !a.roles || (role !== null && a.roles.includes(role))),
    [role],
  );

  const filtered = useMemo(() => {
    const lower = q.trim().toLowerCase();
    if (!lower) return allowed;
    return allowed.filter((a) => a.label.toLowerCase().includes(lower) || a.hint.includes(lower));
  }, [q, allowed]);

  useEffect(() => {
    setActiveIdx(0);
  }, [q]);

  const select = (a: ActionItem) => {
    setOpen(false);
    setQ('');
    void navigate({ to: a.to });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && filtered[activeIdx]) {
      e.preventDefault();
      select(filtered[activeIdx]);
    }
  };

  return (
    <Modal
      opened={open}
      onClose={() => setOpen(false)}
      size="lg"
      withCloseButton={false}
      padding={0}
      overlayProps={{ backgroundOpacity: 0.6, blur: 4 }}
      styles={{
        content: { backgroundColor: colors.card, border: '1px solid #1f1f23' },
      }}
    >
      <Stack gap={0} onKeyDown={onKeyDown}>
        <TextInput
          autoFocus
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          placeholder="Type a command — e.g. 'deploy', 'cost', 'course'"
          leftSection={<Search size={14} />}
          aria-label="Command search"
          styles={{
            input: {
              backgroundColor: colors.card,
              border: 'none',
              borderBottom: '1px solid #1f1f23',
              fontSize: 14,
              padding: '14px 16px 14px 36px',
              height: 48,
            },
            section: { paddingLeft: 14 },
          }}
        />
        <Stack gap={2} p="xs" style={{ maxHeight: 360, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <Text size="sm" c="dimmed" px="md" py="md">
              No matches.
            </Text>
          ) : (
            filtered.map((a, idx) => {
              const active = idx === activeIdx;
              return (
                <button
                  key={a.id}
                  onClick={() => select(a)}
                  style={{
                    background: active ? alpha('neon', 0.08) : 'transparent',
                    border: 'none',
                    borderLeft: `2px solid ${active ? colors.neon : 'transparent'}`,
                    padding: '8px 12px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    width: '100%',
                  }}
                  onMouseEnter={() => setActiveIdx(idx)}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap={10} wrap="nowrap">
                      <span style={{ color: active ? colors.neon : '#a1a1aa' }}>{a.icon}</span>
                      <Text size="sm" c={active ? colors.neon : 'white'}>
                        {a.label}
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed" tt="uppercase">
                      {a.hint}
                    </Text>
                  </Group>
                </button>
              );
            })
          )}
        </Stack>
      </Stack>
    </Modal>
  );
}
